import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import MosqueCreateForm from '../components/mosques/MosqueCreateForm';
import SocietyContextMobileNav from '../components/societies/SocietyContextMobileNav';
import LoadingSpinner from '../components/common/LoadingSpinner';
import useMosqueDetail from '../hooks/useMosqueDetail';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { userCanManageMosque } from '../utils/mosqueAccess';
import { getMosqueUrl, getSlugFromParams } from '../utils/urlHelpers';

function MosqueEditPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { showError } = useToast();
  const societySlugFromRoute = params.societySlug;
  const mosqueSegment = getSlugFromParams(params, 'mosque');
  const { mosque, loading, error } = useMosqueDetail(mosqueSegment);

  useEffect(() => {
    if (authLoading || loading || !mosque || !user || !isAuthenticated) return;
    if (!userCanManageMosque(user, mosque)) {
      showError('You do not have permission to edit this mosque.');
      navigate(getMosqueUrl(mosque, societySlugFromRoute), { replace: true });
    }
  }, [
    authLoading,
    loading,
    mosque,
    user,
    isAuthenticated,
    navigate,
    showError,
    societySlugFromRoute,
  ]);

  if (authLoading) {
    return (
      <PageLayout title="Edit Mosque - PakPoints">
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <LoadingSpinner size="large" />
        </div>
      </PageLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <PageLayout title="Edit Mosque - PakPoints">
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-lg shadow-sm border p-8 max-w-md text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Login required</h2>
            <p className="text-gray-600 mb-6">Sign in to edit this mosque listing.</p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
            >
              Go to login
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (loading) {
    return (
      <PageLayout title="Edit Mosque - PakPoints">
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <LoadingSpinner size="large" />
        </div>
      </PageLayout>
    );
  }

  if (error || !mosque) {
    return (
      <PageLayout title="Mosque Not Found - PakPoints">
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Mosque not found</h1>
            <button
              type="button"
              onClick={() => navigate('/mosques')}
              className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
            >
              Back to mosques
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!userCanManageMosque(user, mosque)) {
    return (
      <PageLayout title="Edit Mosque - PakPoints">
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <LoadingSpinner size="large" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={`Edit ${mosque.name} - PakPoints`}>
      <Helmet>
        <title>Edit Mosque - PakPoints</title>
        <meta name="description" content="Update mosque details and photos" />
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        {societySlugFromRoute ? (
          <SocietyContextMobileNav societySlug={societySlugFromRoute} activeSection="mosques" />
        ) : null}
        <div className="py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <MosqueCreateForm
              mode="edit"
              initialMosque={mosque}
              societySlug={societySlugFromRoute}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

export default MosqueEditPage;
